import { useState, useRef, useEffect } from 'react';
import './QualitativeCollapsed.scss';
import DropdownSelect from '../../DropdownSelect/DropdownSelect';
import { t } from '../../../i18n/i18n';
import {
  getCandidateQualitativeIPVPdfExport,
  getCandidateQualitativePdfExport,
} from '../../../API/services/alkymersService';
import HorizontalLoader from '../../HorizontalLoader/HorizontalLoader';
import CloudDownload from '../../../assets/dashboard/CloudDownload';
import { downloadBase64Pdf, downloadFile } from '../../../utils/downloadFile';
import { QualitativeTypes } from '../../../constants/enums';
import { useSelector } from 'react-redux';

const QualitativeCollapsed = ({ testgroups, recruitSessionId, profile }) => {
  const { user } = useSelector((state) => state.auth);
  const [selectedTestgroup, setSelectedTestgroup] = useState(testgroups[0]);
  const [barElements, setBarElements] = useState({});
  const barRefs = useRef({});
  const finishedQualitative = Boolean(selectedTestgroup.testGroupQualitativeResults?.axisResults);
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => {
    const getBarElements = () => {
      const elements = {};
      Object.keys(barRefs.current).forEach((id) => {
        const element = document.getElementById(id);
        if (element) {
          elements[id] = element;
        }
      });
      setBarElements(elements);
    };
    getBarElements();
    const observer = new MutationObserver(getBarElements);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [selectedTestgroup]);

  const calculateRangeWidth = (section) => {
    const availableWidth = 200;
    const { minPercent, maxPercent } = section;
    const differenceBetweenPercents = Math.abs(minPercent - maxPercent);

    return `${(differenceBetweenPercents * 100) / availableWidth}%`;
  };

  const handleSelectTestgroup = (tg) => {
    setSelectedTestgroup(testgroups.find((item) => item.id === tg.id));
  };

  const isIPVTestgroup = () => {
    return selectedTestgroup.completeData?.qualitativeConfigResults?.type === QualitativeTypes.IPV;
  };

  const calculatePointPosition = (bar) => {
    const result = selectedTestgroup.testGroupQualitativeResults.axisResults.find(
      (result) => result.axisId === bar.id
    );

    if (!result) {
      return 0;
    }

    const parsedRatio = parseFloat(result.ratio);

    if (isIPVTestgroup()) {
      return parsedRatio * 100;
    }
    return (parsedRatio + 1) * 50;
  };
  const generateQualitativeReport = async () => {
    setReportLoading(true);
    try {
      let res = null;
      if (isIPVTestgroup()) {
        res = await getCandidateQualitativeIPVPdfExport({
          recruitSessionId,
          profileId: encodeURI(profile?.id),
          partnerId: user?.id,
        });
        const filename = `${profile.firstName.toLowerCase()}-${profile.lastName.toLowerCase()}.pdf`;
        const blob = new Blob([res], { type: 'application/pdf' });
        downloadFile(blob, filename);
      } else {
        res = await getCandidateQualitativePdfExport({
          recruitSessionId,
          profileId: encodeURI(profile?.id),
          testgroupId: selectedTestgroup?.id,
          demo: false,
        });
        res?.forEach((base64String, index) => {
          const filename = `${profile.firstName.toLowerCase()}-${profile.lastName.toLowerCase()}-qualitative-${
            index + 1
          }.pdf`;
          downloadBase64Pdf(base64String, filename);
        });
      }
    } catch (error) {
      console.error('Error generating qualitative report:', error);
    } finally {
      setReportLoading(false);
    }
  };
  const getQualitativeResult = (sectionId, configId) => {
    return (
      sectionId ==
      selectedTestgroup.testGroupQualitativeResults.axisResults.find(
        (result) => result.axisId === configId
      )?.resultId
    );
  };

  const qualitativeResultsText = (config) => {
    let text = null;
    if (isIPVTestgroup()) {
      const keys = config?.extremes?.[0]?.shortName?.split(',');
      text = selectedTestgroup?.testGroupQualitativeResults?.result?.[`${keys[0]}`]?.[`${keys[1]}`];
    } else {
      text = config?.results?.find((result) => {
        const axisData = selectedTestgroup?.testGroupQualitativeResults?.axisResults?.find(
          (axis) => axis?.axisId === config?.id
        );
        return result?.id === axisData?.resultId;
      }).text;
    }
    return text?.replace('<:name>', profile?.firstName);
  };
  return (
    <div data-testid="qualitative-collapsed-container" className="d-flex flex-column">
      {testgroups.length === 1 ? (
        <div className="d-flex justify-content-between py-2 ">
          <span
            data-testid="qualitative-collapsed-testgroup-name"
            className="my-2  text-muted text-base font-weight-600"
          >
            {selectedTestgroup.name}
          </span>
          {finishedQualitative && (
            <button
              type="button"
              disabled={reportLoading}
              onClick={generateQualitativeReport}
              className="px-3 py-1 text-xs border-blue-principal cursor-pointer d-flex align-items-center font-weight-700  bg-white border-button"
            >
              {!reportLoading ? (
                <>
                  <span className="text-blue-principal  justify-items-center mr-2">
                    {t('DOWNLOAD_QUALITATIVE_REPORT')}
                  </span>
                  <CloudDownload fill="#4987BE" />
                </>
              ) : (
                <HorizontalLoader className="loader-report" />
              )}
            </button>
          )}
        </div>
      ) : (
        <div className=" d-flex flex-column flex-sm-row my-2 justify-content-between align-items-end align-items-sm-center">
          <DropdownSelect
            maxHeight={false}
            className="qualitative-testgroup-select text-xs text-muted"
            setState={handleSelectTestgroup}
            selected={selectedTestgroup.id}
            selectedPlaceholder
            arrowPurple
            arrow={false}
            options={testgroups.map((tg) => ({ id: tg.id, name: tg.name }))}
            key={selectedTestgroup.id}
          />
          {finishedQualitative && (
            <button
              type="button"
              onClick={generateQualitativeReport}
              className="maya-blue px-3 py-1 text-xs border-blue-principal cursor-pointer  d-flex align-items-center font-weight-700  bg-white border-button"
            >
              {!reportLoading ? (
                <>
                  <span className="text-blue-principal  justify-items-center mr-2">
                    {t('DOWNLOAD_QUALITATIVE_REPORT')}
                  </span>
                  <CloudDownload fill="#4987BE" />
                </>
              ) : (
                <HorizontalLoader className="loader-report" />
              )}
            </button>
          )}
        </div>
      )}
      {finishedQualitative ? (
        <div className="d-flex flex-column">
          {selectedTestgroup.completeData.qualitativeConfig.map((config) => (
            <div key={config.id} className="my-2">
              <div className="w-100 d-flex justify-content-between mb-2">
                {isIPVTestgroup() ? (
                  <span
                    key={config.id}
                    data-testid={`qualitative-bar-${0}-extreme`}
                    className={` font-weight-600 text-base ${'qualitative-extreme-selected'}`}
                  >
                    {config.name}
                  </span>
                ) : (
                  config.extremes.map((extreme, index) => (
                    <span
                      key={extreme.id}
                      data-testid={`qualitative-bar-${index}-extreme`}
                      className={` font-weight-600 text-base ${
                        selectedTestgroup.completeData.qualitativeConfigResults &&
                        getQualitativeResult(extreme.id, config.id)
                          ? 'qualitative-extreme-selected'
                          : 'text-light-black'
                      }`}
                    >
                      {extreme.name}
                    </span>
                  ))
                )}
              </div>
              <div id={`${config.id}`} className="qualitative-result-bar position-relative mb-3">
                {config.results.map((section, index) => {
                  const sectionWidth = calculateRangeWidth(section);
                  return (
                    <span
                      key={section.id}
                      data-testid="progress-bar-section"
                      style={{ width: sectionWidth }}
                      className={`qualitative-result-bar ${index === 0 && 'b-left-radius-8'} ${
                        index === config.results.length - 1 && 'b-right-radius-8'
                      } ${
                        index % 2
                          ? 'qualitative-result-bar-color-1'
                          : 'qualitative-result-bar-color-2'
                      }
                      ${
                        getQualitativeResult(section?.id, config?.id) &&
                        'qualitative-result-bar-color-3'
                      }
                      `}
                    />
                  );
                })}
                <span
                  style={{
                    left: `${calculatePointPosition(config)}%`, // Use percentage for positioning
                  }}
                  data-testid="qualitative-result-bar-point"
                  className="qualitative-result-point position-absolute"
                />
              </div>
              <div>
                <span
                  data-testid="qualitative-collapsed-result-description"
                  className=" font-weight-500 text-base text-light-black"
                >
                  {qualitativeResultsText(config)}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <span data-testid="quantitative-collapsed-empty-state" className=" text-xs font-weight-400">
          {t('QUALITATIVE_TEST_EMPTY_STATE')}
        </span>
      )}
    </div>
  );
};

export default QualitativeCollapsed;
